import styled from '@emotion/styled';
import type { TextProps } from '@mantine/core';
// eslint-disable-next-line no-restricted-imports
import { Grid, Title, Text } from '@mantine/core';

type SectionProps = {
  isMobileOnly?: boolean;
  isSidePanel?: boolean;
  chatOffset?: number;
};

const Section = styled.div<SectionProps>(
  ({ theme, isMobileOnly, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
    if (!isMobileOnly || isSidePanel) return {};
    const chatOffset = chatOffsetProp;

    return `
    @media (min-width: ${theme.breakpoints.lg + chatOffset}px) {
      display: none;
    }
  `;
  }
);

const SectionTitle = styled(Title)<{ isSidePanel?: boolean; chatOffset?: number }>(
  ({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
    const chatOffset = chatOffsetProp;

    return `
  margin-bottom: ${theme.spacing.lg}px;

  ${
    isSidePanel
      ? `
    font-size: ${theme.fontSizes.sm}px;
  `
      : `
    @media (max-width: ${theme.breakpoints.sm + chatOffset - 1}px) {
      font-size: ${theme.fontSizes.sm}px;
    }
  `
  }
`;
  }
);

SectionTitle.defaultProps = {
  order: 3,
};

const SectionContent = styled.div<{ isSidePanel?: boolean }>(
  ({ theme, isSidePanel }) => `
    border-radius: ${theme.radius.md}px;
    padding: ${theme.spacing.lg}px;

    ${
      isSidePanel
        ? `
      background: ${theme.colors.gray[0]};
      border: 1px solid ${theme.colors.gray[0]};
    `
        : `
      background: ${theme.white};
      border: 1px solid ${theme.colors.gray[2]};
    `
    }
  `
);

const SectionGrid = styled(Grid)(
  ({ theme }) => `
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing.xs / 2}px;
    margin-top: ${theme.spacing.xs / 2}px;
    border-bottom: 1px solid ${theme.colors.gray[1]};
  `
);

const SectionGridCol = styled(Grid.Col)<{ isSidePanel?: boolean; chatOffset?: number }>(
  ({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
    const chatOffset = chatOffsetProp;

    return `
    &:last-child:not(:first-of-type) {
      display: flex;
      justify-content: end;
      text-align: right;
    }
    & .icon {
      margin-left: ${theme.spacing.md / 2}px;
    }
    ${
      isSidePanel
        ? `
      &:last-child:not(:first-of-type) {
        justify-content: start;
        text-align: left;
      }
    `
        : `
      @media (max-width: ${theme.breakpoints.xs + chatOffset - 1}px) {
        &:last-child {
          justify-content: start;
          text-align: left;
        }
      }
    `
    }
  `;
  }
);

const SectionGridLabel = styled(Text)<
  {
    isSidePanel?: boolean;
    chatOffset?: number;
    dangerouslySetInnerHTML?: { __html: string };
  } & TextProps
>(({ theme, isSidePanel, chatOffset: chatOffsetProp = 0 }) => {
  const chatOffset = chatOffsetProp;

  return `
  font-size: ${theme.fontSizes.md}px;

  & > a {
    color: ${theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7]};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ${
    isSidePanel
      ? `
    font-size: ${theme.fontSizes.sm}px;
  `
      : `
    @media (max-width: ${theme.breakpoints.sm + chatOffset - 1}px) {
      font-size: ${theme.fontSizes.sm}px;
    }
  `
  }
`;
});

const SectionGridValue = styled(SectionGridLabel)`
  font-weight: bold;
  text-align: right;
`;

const SectionNote = styled(Text)<TextProps>(
  ({ theme }) => `
  font-size: ${theme.fontSizes.sm}px;
`
);

export {
  Section,
  SectionTitle,
  SectionContent,
  SectionGrid,
  SectionGridCol,
  SectionGridValue,
  SectionGridLabel,
  SectionNote,
};
